function getForceFreeShippingProductIds(giftBar) {
  const rawProductIds = giftBar.dataset.forceFreeShippingProductIds || "";
  return rawProductIds
    .split(",")
    .map((productId) => productId.trim())
    .filter(Boolean);
}

function isForceFreeShippingCountry(isoCode) {
  return isoCode === "DE" || isoCode === "AT";
}

function syncForceFreeShippingFromCart(giftBar, cart) {
  if (!giftBar || !cart || !Array.isArray(cart.items)) return;

  const forcedProductIds = getForceFreeShippingProductIds(giftBar);
  const countryEligible = isForceFreeShippingCountry(giftBar.dataset.customerCountry);
  const hasForcedFreeShippingProduct = cart.items.some((item) => forcedProductIds.includes(String(item.product_id)));
  giftBar.dataset.forceFreeShippingActive = String(countryEligible && hasForcedFreeShippingProduct);
}

/**
 * @param {element} giftBar
 * @param {GiftConfig} config
 * @param {number} cartSubtotal - Configured subtotal in cents (used for gift thresholds)
 * @param {number} cartSubtotalPostdiscount - Post-discount subtotal in cents (used for shipping threshold)
 */
function updateProgressUI(giftBar, config, cartSubtotal, cartSubtotalPostdiscount) {
  const gifts = theme.CartForm.getActiveSet().gifts;
  const shippingConfig = theme.CartForm.getFreeShipping();
  const remainingStock = theme.CartForm.getRemainingStock();
  const shippingPoint = giftBar.querySelector('[data-gift-type="shipping"]');
  const shippingTierEnabled = Boolean(shippingPoint) && shippingConfig.freeShippingEnabled;
  const forceFreeShippingActive = shippingTierEnabled && giftBar.dataset.forceFreeShippingActive === "true";

  const availibleGifts = gifts.filter((gift) => {
    return gift.gift_variants.some((variant) => {
      const stock = remainingStock.find((stock) => stock.variant_id == variant.variant_id)?.remaining_stock;
      // If stock data is not available, assume the gift is available
      return stock === undefined || stock - gift.buffer_stock > 0;
    });
  });
  const availablePriceGifts = availibleGifts.filter((gift) => {
    return gift.type === "price_point_automatic" || gift.type === "price_point_selectable";
  });

  const unavailibleGifts = gifts.filter((gift) => {
    return gift.gift_variants.every((variant) => {
      const stock = remainingStock.find((stock) => stock.variant_id == variant.variant_id)?.remaining_stock;
      // Only mark as unavailable when we have stock data confirming it's out
      return stock !== undefined && stock - gift.buffer_stock <= 0;
    });
  });

  let highestThreshold = 0;
  availablePriceGifts.forEach((gift) => {
    const thresholdCents = gift.threshold_amount * 100;
    if (thresholdCents > highestThreshold) {
      highestThreshold = thresholdCents;
    }
  });

  if (shippingTierEnabled) {
    const shippingThreshold = shippingConfig.freeShippingThreshold * 100;
    if (shippingThreshold > highestThreshold) {
      highestThreshold = shippingThreshold;
    }
  }

  let progressPercent = 0;
  if (highestThreshold > 0) {
    progressPercent = (cartSubtotal * 100) / highestThreshold;
    if (progressPercent > 100) {
      progressPercent = 100;
    }
  }

  // Cap bar at shipping checkpoint when shipping is not yet reached (post-discount).
  // Prevents bar from visually passing an inactive shipping icon when discounts are active.
  if (shippingTierEnabled) {
    const shippingCapCents = shippingConfig.freeShippingThreshold * 100;
    if (cartSubtotalPostdiscount < shippingCapCents && highestThreshold > 0 && !forceFreeShippingActive) {
      const shippingPositionPercent = (shippingCapCents / highestThreshold) * 100;
      if (progressPercent > shippingPositionPercent) {
        progressPercent = shippingPositionPercent;
      }
    }

    if (forceFreeShippingActive && highestThreshold > 0) {
      const shippingPositionPercent = (shippingCapCents / highestThreshold) * 100;
      if (progressPercent < shippingPositionPercent) {
        progressPercent = shippingPositionPercent;
      }
    }
  }

  // Update progress bar width
  const progressElement = giftBar.querySelector(".cart-gifts__progress");
  const filledBar = giftBar.querySelector(".cart-gifts__progress-bar--filled");
  progressElement.style.display = highestThreshold > 0 ? "" : "none";
  filledBar.style.width = `${progressPercent}%`;

  // get next gift threshold that hasn't been reached yet
  const nextGift = availablePriceGifts
    .slice()
    .sort((a, b) => a.threshold_amount - b.threshold_amount)
    .find((gift) => {
      return cartSubtotal < gift.threshold_amount * 100;
    });

  // Check if free shipping is the next unreached milestone (uses discounted subtotal)
  const shippingThresholdCents = shippingTierEnabled ? shippingConfig.freeShippingThreshold * 100 : null;
  const shippingNotReached = shippingThresholdCents && cartSubtotalPostdiscount < shippingThresholdCents && !forceFreeShippingActive;
  const nextGiftThresholdCents = nextGift ? nextGift.threshold_amount * 100 : null;

  // Determine which milestone comes next: gift or free shipping
  const nextIsShipping = shippingNotReached && (!nextGiftThresholdCents || shippingThresholdCents <= nextGiftThresholdCents);

  // Update progress text based on next milestone
  const progressTextElement = giftBar.querySelector(".cart-gifts__progress-text");
  if (nextIsShipping) {
    const amountNeeded = (shippingThresholdCents - cartSubtotalPostdiscount) / 100;
    progressTextElement.innerHTML = /* HTML */ `Noch
      <span class="cart-gifts__progress-amount">${theme.Currency.formatMoney(amountNeeded * 100, theme.settings.money_format)}</span> bis zum kostenfreien
      Versand.`;
  } else if (nextGift) {
    const amountNeeded = (nextGiftThresholdCents - cartSubtotal) / 100;
    const sortedGifts = availablePriceGifts.slice().sort((a, b) => a.threshold_amount - b.threshold_amount);
    const giftIndex = sortedGifts.indexOf(nextGift);
    const ordinals = ["", "zweiten", "dritten", "vierten", "fünften"];
    const ordinal = ordinals[giftIndex] || "";
    const giftLabel = ordinal ? `zu deinem ${ordinal} Geschenk.` : "bis zum Geschenk.";
    progressTextElement.innerHTML = /* HTML */ `Noch
      <span class="cart-gifts__progress-amount">${theme.Currency.formatMoney(amountNeeded * 100, theme.settings.money_format)}</span> ${giftLabel}`;
  } else {
    progressTextElement.innerHTML = /* HTML */ `Du hast alle Geschenke freigeschaltet!`;
  }

  const points = giftBar.querySelectorAll("[data-point]");
  points.forEach((point, index) => {
    const value = parseInt(point.dataset.threshold);
    const positionPercent = highestThreshold > 0 ? (value / highestThreshold) * 100 : 0;
    const adjustingValue = index === points.length - 1 ? -8.5 : -11.5;
    point.style.left = `clamp(12.5px, calc(${positionPercent}% + ${adjustingValue}px), calc(100% - 12.5px))`;

    // Shipping uses the post-discount subtotal; gifts use the configured subtotal.
    const subtotalForPoint = point.dataset.giftType === "shipping" ? cartSubtotalPostdiscount : cartSubtotal;
    if (point.dataset.giftType === "shipping" && forceFreeShippingActive) {
      point.classList.add("active");
      return;
    }
    if (subtotalForPoint >= value) {
      point.classList.add("active");
    } else {
      point.classList.remove("active");
    }
  });

  // show/hide gift selection dropdowns based on whether their thresholds have been reached
  availablePriceGifts.forEach((gift) => {
    const selection = giftBar.querySelector(`.cart-gifts__selection[data-gift-id="${gift.id}"]`);
    if (selection) {
      if (cartSubtotal >= gift.threshold_amount * 100) {
        selection.style.display = "block";
      } else {
        selection.style.display = "none";
        selection.removeAttribute("open");
      }
    }
  });

  // hide gift option that have no remaining stock
  availibleGifts.forEach((gift) => {
    const giftVariantIds = gift.gift_variants.map((variant) => String(variant.variant_id));
    const selection = giftBar.querySelector(`.cart-gifts__selection[data-gift-id="${gift.id}"]`);
    if (selection) {
      const options = selection.querySelectorAll(".cart-gifts__selection-option");
      const isAutomatic = selection.classList.contains("cart-gifts__selection--automatic");
      let automaticChoice = null;
      options.forEach((option) => {
        const variantId = option.dataset.product;
        if (giftVariantIds.includes(variantId)) {
          const stock = remainingStock.find((stock) => stock.variant_id == variantId)?.remaining_stock;
          const inStock = stock === undefined || stock - gift.buffer_stock > 0;
          const optionRow = option.closest('[role="option"]');
          if (!optionRow) return;

          // Automatic gifts use the first in-stock variant only. Later entries are backups
          // and stay hidden, including when their stock is above the buffer.
          if (isAutomatic) {
            if (inStock && !automaticChoice) {
              automaticChoice = option;
              optionRow.style.display = "";
            } else {
              optionRow.style.display = "none";
            }
            return;
          }

          if (stock !== undefined) {
            optionRow.style.display = inStock ? "" : "none";
          }
        }
      });

      if (isAutomatic && automaticChoice) {
        const label = selection.querySelector(".cart-gifts__selection-label");
        const selectedImageContainer = selection.querySelector(".cart-gifts__selection-selected-image");
        if (label) {
          label.textContent = automaticChoice.querySelector("span").textContent.trim();
        }
        if (selectedImageContainer) {
          const img = automaticChoice.querySelector("img");
          selectedImageContainer.innerHTML = img ? img.outerHTML : "";
        }
      }
    }
  });

  // remove gift selection and checkpoint
  unavailibleGifts.forEach((gift) => {
    const selection = giftBar.querySelector(`.cart-gifts__selection[data-gift-id="${gift.id}"]`);
    if (selection) {
      selection.style.display = "none";
    }

    const checkpoint = giftBar.querySelector(`.cart-gifts__progress-point[data-gift-id="${gift.id}"]`);
    if (checkpoint) {
      checkpoint.style.display = "none";
    }
  });
}

function updateGiftSelectionUIForGift(giftBar, giftId, selectedVariantId) {
  const selection = giftBar.querySelector(`.cart-gifts__selection[data-gift-id="${giftId}"]`);
  if (!selection) return;

  const normalizedSelectedVariantId = normalizeGiftVariantId(selectedVariantId);

  // Update aria-selected on <li role="option"> and capture selected label in one pass
  let selectedLabel = "";
  const options = Array.from(selection.querySelectorAll(".cart-gifts__selection-option"));
  options.forEach((option) => {
    const isSelected = normalizeGiftVariantId(option.dataset.product) === normalizedSelectedVariantId;
    option.closest('[role="option"]').setAttribute("aria-selected", String(isSelected));
    if (isSelected) {
      selectedLabel = option.textContent.trim();
    }
  });

  // Update label text and close dropdown
  const label = selection.querySelector(".cart-gifts__selection-label");
  if (label && selectedLabel) {
    label.textContent = selectedLabel;
  }
  selection.removeAttribute("open");

  // Update selected image in toggle
  const selectedImageContainer = selection.querySelector(".cart-gifts__selection-selected-image");
  const selectedOption = options.find((option) => normalizeGiftVariantId(option.dataset.product) === normalizedSelectedVariantId);
  if (selectedImageContainer && selectedOption) {
    const optionImage = selectedOption.querySelector("img");
    if (optionImage) {
      selectedImageContainer.innerHTML = optionImage.outerHTML;
    } else {
      selectedImageContainer.innerHTML = "";
    }
  }
}

function normalizeGiftVariantId(variantId) {
  return String(variantId || "").replace("gid://shopify/ProductVariant/", "");
}

function getGiftProxyState(giftBar) {
  if (!window.NDLGiftProxyState) {
    window.NDLGiftProxyState = {
      chain: Promise.resolve(),
      lastGiftSelection: [],
      lastSyncedCartSignature: null,
    };
  }
  return window.NDLGiftProxyState;
}

function getGiftProxyCartSignature(cart) {
  return JSON.stringify({
    token: cart.token,
    item_count: cart.item_count,
    total_price: cart.total_price,
    original_total_price: cart.original_total_price,
    items_subtotal_price: cart.items_subtotal_price,
    total_discount: cart.total_discount,
    discount_codes: (cart.discount_codes || [])
      .filter((discount) => discount && discount.applicable && discount.code)
      .map((discount) => String(discount.code).trim().toUpperCase())
      .sort(),
    items: (cart.items || []).map((item) => ({
      key: item.key,
      variant_id: item.variant_id,
      quantity: item.quantity,
      final_line_price: item.final_line_price,
    })),
  });
}

async function getGiftProxyCart() {
  const cart = await theme.cart.getCart();
  if (!cart || !cart.token) {
    throw new Error("Missing cart token");
  }
  return cart;
}

function getGiftProxyUrl(giftBar, token) {
  const url = new URL(giftBar.dataset.giftProxyUrl || "/apps/gifts", window.location.origin);
  url.searchParams.set("token", token);
  if (giftBar.dataset.customerId) {
    url.searchParams.set("logged_in_customer_id", giftBar.dataset.customerId);
  }
  return url.toString();
}

function normalizeGiftSelectionResponse(data) {
  if (!data) return [];
  if (Array.isArray(data.giftSelection)) return data.giftSelection;
  if (Array.isArray(data.giftSelectionNew)) return data.giftSelectionNew;
  if (Array.isArray(data.gift_selection)) return data.gift_selection;
  return [];
}

function reconcileConditionGiftVisibility(giftBar, giftSelection) {
  const selectedGiftIds = new Set(
    (Array.isArray(giftSelection) ? giftSelection : []).filter((gift) => gift && gift.gift_id).map((gift) => String(gift.gift_id)),
  );

  giftBar.querySelectorAll("[data-condition-gift]").forEach((selection) => {
    const isEligible = selectedGiftIds.has(String(selection.dataset.giftId));
    selection.style.display = isEligible ? "block" : "none";
    if (!isEligible) {
      selection.removeAttribute("open");
    }
  });
}

function applyGiftSelectionUI(giftBar, giftSelection) {
  if (!Array.isArray(giftSelection)) return;

  reconcileConditionGiftVisibility(giftBar, giftSelection);
  giftSelection.forEach((gift) => {
    if (!gift || !gift.gift_id || !gift.selected_variant_id) return;
    updateGiftSelectionUIForGift(giftBar, String(gift.gift_id), String(gift.selected_variant_id));
  });
}

function getFallbackGiftSelection(payload) {
  if (!payload || payload.type !== "select_gift") return [];

  return [
    {
      gift_id: String(payload.gift_id),
      selected_variant_id: String(payload.selected_variant_id),
    },
  ];
}

async function runGiftProxySync(giftBar, payload, options = {}) {
  const cartBeforeSync = await getGiftProxyCart();
  const state = getGiftProxyState(giftBar);
  const cartSignatureBeforeSync = getGiftProxyCartSignature(cartBeforeSync);

  // A page-load cart refresh can emit cart:updated after the initial sync
  // without changing the cart. Do not make an identical proxy request, but
  // allow the sync when the cart actually changed (for example, an item was
  // removed because it became unavailable).
  if (payload.type === "cart_update" && state.lastSyncedCartSignature === cartSignatureBeforeSync) {
    return { skipped: true };
  }

  const response = await fetch(getGiftProxyUrl(giftBar, cartBeforeSync.token), {
    method: "POST",
    credentials: "same-origin",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    body: JSON.stringify(payload),
  });

  let data = null;
  try {
    data = await response.json();
  } catch (error) {
    if (response.ok) {
      throw error;
    }
  }

  // The proxy can return HTTP 200 while reporting a failed Shopify GraphQL
  // operation, so do not treat that response as a successful cart sync.
  if (!response.ok || data?.success === false || data?.error) {
    throw new Error(data?.error || "Gift proxy request failed");
  }

  const returnedGiftSelection = normalizeGiftSelectionResponse(data);
  state.lastGiftSelection = returnedGiftSelection.length ? returnedGiftSelection : getFallbackGiftSelection(payload);
  applyGiftSelectionUI(giftBar, state.lastGiftSelection);

  if (options.rebuildCart) {
    const cart = await getGiftProxyCart();
    const cartSignatureAfterSync = getGiftProxyCartSignature(cart);
    state.lastSyncedCartSignature = cartSignatureAfterSync;

    if (cartSignatureAfterSync !== cartSignatureBeforeSync) {
      document.dispatchEvent(
        new CustomEvent("cart:updated", {
          bubbles: true,
          detail: {
            cart,
            source: "gift-proxy",
          },
        }),
      );
    }
  } else {
    state.lastSyncedCartSignature = cartSignatureBeforeSync;
  }

  return data;
}

function queueGiftProxySync(giftBar, payload, options = {}) {
  const state = getGiftProxyState(giftBar);
  document.dispatchEvent(new CustomEvent("gift:proxy-sync-start"));

  state.chain = state.chain
    .catch(() => {})
    .then(() => runGiftProxySync(giftBar, payload, options))
    .catch((error) => {
      console.error("Gift proxy sync failed:", error);
      throw error;
    });

  state.chain.then(
    () => document.dispatchEvent(new CustomEvent("gift:proxy-sync-settled")),
    () => document.dispatchEvent(new CustomEvent("gift:proxy-sync-settled")),
  );

  return state.chain;
}

async function initializeNDLGiftBar() {
  const giftBar = document.querySelector('.cart-gifts[data-version="v4"]');
  if (!giftBar || giftBar.dataset.ndlGiftBarInitialized === "true") return;

  const config = theme.CartForm.getGiftConfig(giftBar);
  if (!config || !config.enabled) return;
  giftBar.dataset.ndlGiftBarInitialized = "true";

  const cartSubtotal = parseInt(giftBar.dataset.cartSubtotal);
  const cartSubtotalPostdiscount = parseInt(giftBar.dataset.cartSubtotalPostdiscount);

  console.log("Initial gift bar render with subtotal:", cartSubtotal, "and post-discount subtotal:", cartSubtotalPostdiscount);

  // Block cart:updated from re-rendering during init to prevent a brief flash
  giftBar._initializing = true;

  // updateProgressUI will handle the initial rendering of the progress bar and text
  updateProgressUI(giftBar, config, cartSubtotal, cartSubtotalPostdiscount);

  // Reveal progress section now that JS has set the correct state.
  // Disable transition for the first paint so it snaps instantly.
  const filledBar = giftBar.querySelector(".cart-gifts__progress-bar--filled");
  filledBar.style.transition = "none";
  giftBar.classList.add("cart-gifts--ready");
  // Force reflow then re-enable transition for subsequent updates
  filledBar.offsetWidth;
  filledBar.style.transition = "";

  if (!window.NDLGiftProxyEventsBound) {
    window.NDLGiftProxyEventsBound = true;
    document.addEventListener("click", function (event) {
      const button = event.target.closest('.cart-gifts[data-version="v4"] .cart-gifts__selection-option');
      if (!button) return;

      const currentGiftBar = button.closest('.cart-gifts[data-version="v4"]');
      const variantId = button.dataset.product;
      const giftId = button.dataset.giftId;

      if (!currentGiftBar || !giftId || !variantId) return;

      queueGiftProxySync(
        currentGiftBar,
        {
          type: "select_gift",
          gift_id: giftId,
          selected_variant_id: variantId,
        },
        { rebuildCart: true },
      ).catch((error) => {
        console.warn("Gift proxy sync failed after gift selection:", error);
      });
    });
  }

  queueGiftProxySync(giftBar, { type: "cart_update" }, { rebuildCart: true }).catch((error) => {
    console.warn("Initial gift proxy sync failed:", error);
  });

  // Clear _initializing after the first full cart rebuild cycle completes.
  // Every cart:updated triggers cart:build → buildCart → cartMarkup → gift:subtotal-changed,
  // so the first gift:subtotal-changed signals the cart state is consistent.
  function clearInitFlag() {
    giftBar._initializing = false;
    document.removeEventListener("gift:subtotal-changed", clearInitFlag);
  }
  document.addEventListener("gift:subtotal-changed", clearInitFlag);
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initializeNDLGiftBar, { once: true });
} else {
  initializeNDLGiftBar();
}
document.addEventListener("nh:cart-drawer-mounted", initializeNDLGiftBar);

document.addEventListener("cart:updated", async function (event) {
  const giftBar = document.querySelector('.cart-gifts[data-version="v4"]');
  if (!giftBar) return;

  syncForceFreeShippingFromCart(giftBar, event.detail?.cart);

  if (event.detail?.source === "gift-proxy") return;
  if (giftBar._initializing) return;

  queueGiftProxySync(giftBar, { type: "cart_update" }, { rebuildCart: true }).catch((error) => {
    console.warn("Gift proxy sync failed after cart update:", error);
  });

  // Skip UI update here — the cart:updated response can have inconsistent
  // data (e.g. items reflect new qty but total_price reflects old state).
  // Instead, wait for gift:subtotal-changed which comes from server-rendered
  // markup and is always consistent. We only update the selection container.
  const selectionContainer = document.querySelector(".cart-gifts__selection-container");
  selectionContainer.classList.remove("cart-gifts__selection-container--hidden");
  selectionContainer.classList.add("cart-gifts__selection-container--visible");
});

document.addEventListener("gift:subtotal-changed", function (event) {
  const giftBar = document.querySelector('.cart-gifts[data-version="v4"]');
  if (!giftBar) return;

  const config = theme.CartForm.getGiftConfig(giftBar);
  if (!config || !config.enabled) return;

  const cartSubtotalPostdiscount = event.detail.postdiscountSubtotal;
  const cartSubtotal =
    config.usePreDiscountCartValue === false
      ? (event.detail.giftPostdiscountSubtotal ?? cartSubtotalPostdiscount)
      : (event.detail.giftPrediscountSubtotal ?? event.detail.prediscountSubtotal);
  updateProgressUI(giftBar, config, cartSubtotal, cartSubtotalPostdiscount);
  applyGiftSelectionUI(giftBar, getGiftProxyState(giftBar).lastGiftSelection);

  const selectionContainer = document.querySelector(".cart-gifts__selection-container");
  selectionContainer.classList.remove("cart-gifts__selection-container--hidden");
  selectionContainer.classList.add("cart-gifts__selection-container--visible");
});
